import { Box, Typography } from '@mui/material';
import { FC, LegacyRef, useEffect, useMemo, useState } from 'react';
import { useZxing } from 'react-zxing';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import useCameras, { mediaStreamConstraintsHD } from 'hooks/useCameras';
import SwitchCameraIcon from 'components/SwitchCameraIcon';
import cloneDeep from 'lodash.clonedeep';
import { Loading } from 'components/Loading/Loading';

type TBarcodeScanner = FC<{
  onDetected: (code: string) => void;
}>;

const DEBUG_BARCODE_SCANNING = false;

const barcodeHints = new Map();
barcodeHints.set(DecodeHintType.POSSIBLE_FORMATS, [
  // --- 1D (Kits, Trays, and Warehouse Shelving) ---
  BarcodeFormat.ITF, // Common on corrugated warehouse boxes
  BarcodeFormat.CODE_128, // Standard for GS1-128 (Logistics & outer cartons)
  BarcodeFormat.CODE_39, // Legacy T-DOC and surgical sets
  BarcodeFormat.EAN_13, // Standard retail/supplier products (GTINs)
  BarcodeFormat.UPC_EAN_EXTENSION, // Common on retail products (GTINs)

  // --- 2D (Surgical Parts and Modern Identification) ---
  BarcodeFormat.DATA_MATRIX, // THE standard for etched surgical instruments
  BarcodeFormat.QR_CODE, // General supplier info / QR for kits
  BarcodeFormat.PDF_417, // Used on some shipping labels/patient IDs
]);
//barcodeHints.set(DecodeHintType.TRY_HARDER, true);

// barcode scanner component using react-zxing
// https://www.npmjs.com/package/react-zxing
const BarcodeScanner: TBarcodeScanner = ({ onDetected }) => {
  const { camera, cameras, switchCamera, negotiatedResolution } = useCameras(mediaStreamConstraintsHD);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  const constraints = useMemo(() => {
    const mediaConstraints: MediaStreamConstraints = cloneDeep(mediaStreamConstraintsHD);

    const videoConstraints = mediaConstraints.video as MediaTrackConstraints;
    if (camera?.deviceId) {
      videoConstraints.deviceId = { exact: camera.deviceId };
    }

    if (negotiatedResolution) {
      videoConstraints.width = { exact: negotiatedResolution.width };
      videoConstraints.height = { exact: negotiatedResolution.height };
    }

    return mediaConstraints;
  }, [camera?.deviceId, negotiatedResolution]);

  const { ref } = useZxing({
    paused: !camera?.deviceId,
    onDecodeResult(result) {
      const barcode = result.getText();
      onDetected(barcode);
      if (DEBUG_BARCODE_SCANNING) {
        console.log('Barcode', result.getBarcodeFormat(), barcode);
      }
    },
    constraints,
    //hints: barcodeHints,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (!ref.current || !DEBUG_BARCODE_SCANNING) return;

      const { videoWidth, videoHeight } = ref.current;
      const settings = (ref.current.srcObject as MediaStream)?.getVideoTracks()[0]?.getSettings();
      console.table({
        deviceId: settings?.deviceId || 'N/A',
        'Requested Res': negotiatedResolution
          ? `${negotiatedResolution.width}x${negotiatedResolution.height}`
          : 'Ideal',
        'Actual Res': `${videoWidth}x${videoHeight}`,
        'Focus Mode': (settings as any)?.focusMode || 'N/A',
        'Aspect Ratio': videoWidth && videoHeight ? (videoWidth / videoHeight).toFixed(2) : 'N/A',
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [ref, negotiatedResolution]);

  const videoConstraints = mediaStreamConstraintsHD.video as MediaTrackConstraints;
  const minRecommendedWidth = (videoConstraints?.width as ConstrainULongRange)?.min || 0;
  const minRecommendedHeight = (videoConstraints?.height as ConstrainULongRange)?.min || 0;

  const { videoWidth = 0, videoHeight = 0 } = ref?.current || {};

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', position: 'relative' }}>
        {isVideoLoading && <Loading size={30} />}
        <video
          ref={ref as LegacyRef<HTMLVideoElement> | undefined}
          onPlaying={() => setIsVideoLoading(false)}
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '290px',
            objectFit: 'contain',
            visibility: isVideoLoading ? 'hidden' : 'visible',
          }}
        />

        {cameras && cameras.length > 1 && <SwitchCameraIcon switchCamera={switchCamera} sx={{ right: '64px' }} />}
      </Box>
      {videoWidth && videoWidth < minRecommendedWidth ? (
        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
          Your camera resolution is {videoWidth}x{videoHeight}, the recommended minimum is {minRecommendedWidth}x
          {minRecommendedHeight}. The barcode reader may not be effective.
        </Typography>
      ) : null}
    </>
  );
};

export default BarcodeScanner;
